import React from 'react';
import { Button } from 'antd';
import { TEligibilityResult } from 'types/insuranceEligibility';
import { anyPriceReported, formatCents, formatPlanDate } from '../derive';
import type { TBarScene } from '../derive';
import { INFO_COPY, TILE_LABELS } from '../copy';
import s from '../s.module.less';

type Props = {
    result: TEligibilityResult;
    scene: TBarScene;
    // 271 payer 名（已过 payerProse）：SNC 行点名用
    payerName?: string;
    // oon / nonCovered 的唯一动作：档案上没有错字段、没有可要的文件，剩下的是一场对话（原型）
    onMessagePatient?: () => void;
    // plan type 不在 template 的 OON（10/05）：template 归谁（"your" / "this provider’s"）与补 template 的入口。
    // practice 视图下 owner 改不了别人的 template，不给按钮（与 TemplateGapNote 同口径）
    templateWhose?: string;
    onAddToTemplate?: () => void;
};

// 结果卡的说明行集合：oon 警告、未定价两句、冲突明细、排除说明、prior auth、payer notes。
// inactive 的两段文字也在这里（tiles 不渲染时它是卡片主体）
const InfoLines: React.FC<Props> = ({ result, scene, payerName, onMessagePatient, templateWhose, onAddToTemplate }) => {
    const lines: React.ReactNode[] = [];

    const messageBtn = (key: string) =>
        onMessagePatient ? (
            <div key={key} className={s.fixActions}>
                <Button type="primary" onClick={onMessagePatient}>
                    {INFO_COPY.messagePatientBtn}
                </Button>
            </div>
        ) : null;

    if (scene === 'inactive') {
        // bar 已说「没生效」，这两句只说后果——普通灰行（非红警示行），日期加粗（原型 elig-kv）
        const end = result.plan?.endDate;
        lines.push(
            <div key="inactive1" className={s.infoLine}>
                {end && (
                    <>
                        {INFO_COPY.inactiveEndedLead}
                        <b>{formatPlanDate(end)}</b>
                        {'. '}
                    </>
                )}
                {INFO_COPY.inactive1}
            </div>,
            <div key="inactive2" className={s.infoLine}>
                {INFO_COPY.inactive2}
            </div>,
        );
        return <div className={s.infoLines}>{lines}</div>;
    }

    const planType = scene === 'oon' ? result.network?.planTypeNotOnTemplate : undefined;
    if (planType) {
        // 平台按 service template 判的 OON：首句红色点名原因（不是 payer 说的），次句灰色给两条出路
        lines.push(
            <div key="pt1" className={`${s.infoLine} ${s.infoLineBad}`}>
                {INFO_COPY.planTypeLead}
                <b>{planType}</b>
                {INFO_COPY.planTypeTail(templateWhose || INFO_COPY.planTypeWhoseSelf)}
            </div>,
            <div key="pt2" className={s.infoLine}>
                {INFO_COPY.planTypeNext(
                    payerName || 'The payer',
                    planType,
                    templateWhose === INFO_COPY.planTypeWhoseOther,
                )}
            </div>,
        );
        if (onMessagePatient || onAddToTemplate) {
            lines.push(
                <div key="pt-acts" className={s.fixActions}>
                    {onMessagePatient && (
                        <Button type="primary" onClick={onMessagePatient}>
                            {INFO_COPY.messagePatientBtn}
                        </Button>
                    )}
                    {onAddToTemplate && <Button onClick={onAddToTemplate}>{INFO_COPY.addToTemplateBtn}</Button>}
                </div>,
            );
        }
    } else if (scene === 'oon') {
        // 普通灰行，不是红警示行：原型这句挂的是 elig-kv + elig-warn，而 .elig-kv 写在 .elig-warn
        // 之后、权重相同，颜色取灰——屏幕上的原型就是灰的。红留给 bar，句子只负责说事实
        lines.push(
            <div key="oon" className={s.infoLine}>
                {INFO_COPY.oonLead(payerName || 'This payer')}
                <b>{INFO_COPY.oonBold}</b>
                {INFO_COPY.oonTail}
            </div>,
        );
        const btn = messageBtn('oon-msg');
        if (btn) lines.push(btn);
    }

    if (scene === 'nonCovered') {
        // 同 OON：原型这句也是 elig-kv + elig-warn，渲染出来是灰的。红只在 bar 上，说明行说事实
        lines.push(
            <div key="nc1" className={s.infoLine}>
                {INFO_COPY.sncLead(payerName || 'This payer')}
                <b>{INFO_COPY.sncNotCover}</b>
                {INFO_COPY.sncTail}
            </div>,
        );
        if (result.serviceNotCovered?.note) {
            lines.push(
                <div key="nc2" className={s.infoLine}>
                    {INFO_COPY.sncNote(result.serviceNotCovered.note)}
                </div>,
            );
        }
        lines.push(
            <div key="nc3" className={s.infoLine}>
                {INFO_COPY.sncSelfPay}
            </div>,
        );
        const btn = messageBtn('nc-msg');
        if (btn) lines.push(btn);
    }

    if (scene === 'conflict' && result.costSharing) {
        const conflicted = (
            [
                [TILE_LABELS.copay, result.costSharing.copay],
                [TILE_LABELS.coinsurance, result.costSharing.coinsurance],
            ] as const
        ).filter(([, t]) => (t.values?.length || 0) > 1);
        // 标签告诫句整面板只说一次（copay 与 coinsurance 同时冲突时，逐组各说一遍是同一句连着重复）
        const anyLabeled = conflicted.some(([, t]) => t.valueLabels?.some(Boolean));
        lines.push(
            <div key="conflict" className={s.infoLine}>
                {INFO_COPY.conflictIntro}
                {conflicted.map(([label, t]) => (
                    <div key={label} className={s.conflictRows}>
                        {/* 每行带 payer 原文标签（valueLabels 与 values 对齐）；没附标签的行也明说——
                            "sent no label" 与 "labelled …" 并排时缺席本身就是信息 */}
                        {(t.values || []).map((v, i) => {
                            const rowLabel = t.valueLabels?.[i];
                            return (
                                // eslint-disable-next-line react/no-array-index-key
                                <div key={`${v}-${i}`} className={s.conflictRow}>
                                    <span className={s.conflictAmt}>
                                        {t.kind === 'PERCENT' ? `${v}%` : formatCents(v)}
                                    </span>
                                    <span className={`${s.conflictNote} ${rowLabel ? '' : s.conflictNoteNone}`}>
                                        {rowLabel ? INFO_COPY.conflictRowLabel(rowLabel) : INFO_COPY.conflictRowNoLabel}
                                    </span>
                                </div>
                            );
                        })}
                        {/* 这句按组说：描述的是本组各行之间还有哪些轴是一致的 */}
                        <div className={s.conflictShared}>{INFO_COPY.conflictShared}</div>
                    </div>
                ))}
                {/* 标签是 payer 原话：可辅助判断，但不能替 payer 选一个报给患者 */}
                {anyLabeled && <div className={s.conflictShared}>{INFO_COPY.conflictLabelNote}</div>}
            </div>,
        );
    }

    // 「payer 答了这个 plan，但没给这次问诊的价格」两句。原型的条件是 !priced，而它的 priced
    // 声明在使用之后（var 提升），实际恒 falsy，于是这两句在任何非 SNC/冲突/carve 的结果上都印，
    // 包括价格齐全的 active——那是 bug。这里取它的本意：没有 per-visit 价格才印。
    // 于是 PLANWIDE_ONLY（后端定义里 copay/coinsurance 必缺席）照印，ACTIVE 不印
    const unpriced = !!result.costSharing && !anyPriceReported(result.costSharing);
    if (scene === 'partial' || (scene === 'active' && unpriced)) {
        lines.push(
            <div key="p1" className={s.infoLine}>
                {INFO_COPY.partial1}
            </div>,
            <div key="p2" className={s.infoLine}>
                {INFO_COPY.partial2}
            </div>,
        );
    }

    if (scene === 'unknown') {
        lines.push(
            <div key="unknown" className={s.infoLine}>
                {INFO_COPY.unknown}
            </div>,
        );
    }

    (result.notes || []).forEach((n, i) => {
        lines.push(
            // eslint-disable-next-line react/no-array-index-key
            <div key={`note-${i}`} className={s.infoNote}>
                {n}
            </div>,
        );
    });

    if (!lines.length) {
        return null;
    }
    return <div className={s.infoLines}>{lines}</div>;
};

export default InfoLines;
